/**
 * Needs-Attention Service
 *
 * HTTP wrappers for the "Needs attention" tray (issue #322, B.2).
 *
 * Endpoints (all tenant-scoped, role enforced server-side):
 *   GET    /api/imports/needs-attention?source=&type=&page=&limit=
 *   POST   /api/imports/needs-attention/:txId/resolve       body: { resolution }
 *   POST   /api/imports/needs-attention/bulk-resolve        body: { txIds, resolution }
 *
 * Contract types live in `@bat/shared` (single source of truth).
 */

import type {
  AttentionWarningType,
  ListNeedsAttentionResponse,
  ResolveAction,
  ResolveOneResponse,
  BulkResolveBody,
  BulkResolveResult,
  ReprocessUnknownRowsRequest,
  ReprocessUnknownRowsResponse,
} from '@bat/shared'

import { api, throwBackendError } from '@/platform/api/api.service'
import { IMPORT_ROUTES } from '@/shared/constants/config-routes'

// ---------------------------------------------------------------------------
// Route constants — centralized in `IMPORT_ROUTES.NEEDS_ATTENTION`; aliased
// here so the service methods below keep their original call sites.
// ---------------------------------------------------------------------------

const NEEDS_ATTENTION_ROUTES = IMPORT_ROUTES.NEEDS_ATTENTION

// ---------------------------------------------------------------------------
// Filter shape consumed by the React Query hook. Mirrors the backend query
// schema but allows `undefined` so callers can omit fields cleanly.
// ---------------------------------------------------------------------------

export interface NeedsAttentionFilters {
  source?: string
  type?: AttentionWarningType
  page?: number
  limit?: number
}

/**
 * Build a clean query string (drop undefined/null values) for the GET call.
 * Returned shape matches the `api.get` second-argument contract — keys map
 * to scalar values only.
 */
function buildListParams(filters: NeedsAttentionFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {}
  if (filters.source !== undefined && filters.source !== '') {
    params.source = filters.source
  }
  if (filters.type !== undefined) {
    params.type = filters.type
  }
  if (filters.page !== undefined) {
    params.page = filters.page
  }
  if (filters.limit !== undefined) {
    params.limit = filters.limit
  }
  return params
}

/**
 * List the current tenant's needs-attention rows.
 *
 * Throws via `throwBackendError` when the API returns `{ success: false }`
 * so React Query can surface the error to the UI (`isError`).
 */
export const listNeedsAttention = async (
  filters: NeedsAttentionFilters = {},
): Promise<ListNeedsAttentionResponse> => {
  const response = await api.get<ListNeedsAttentionResponse>(
    NEEDS_ATTENTION_ROUTES.LIST,
    buildListParams(filters),
  )

  if (!response.success || !response.data) {
    throwBackendError(response, 'NEEDS_ATTENTION_LIST_FAILED')
  }

  return response.data
}

/**
 * Resolve a single staging transaction (map_asset or discard).
 *
 * `txId` here is the staging row id (`imported_transactions.id`, i.e.
 * `AttentionRow.id`), NOT the ledger `transactions.id`
 * (`AttentionRow.transactionId`). The backend `:txId` route matches
 * `imported_transactions.id`, so passing the ledger id 404s.
 */
export const resolveOne = async (
  stagingId: string,
  resolution: ResolveAction,
): Promise<ResolveOneResponse> => {
  const txId = stagingId
  const response = await api.post<ResolveOneResponse>(NEEDS_ATTENTION_ROUTES.RESOLVE_ONE(txId), {
    resolution,
  })

  if (!response.success || !response.data) {
    throwBackendError(response, 'NEEDS_ATTENTION_RESOLVE_FAILED')
  }

  return response.data
}

/**
 * Bulk-resolve N ledger transactions with the SAME resolution payload.
 *
 * The `map_asset` form is only useful when every selected row shares the
 * same `from` ticker — the UI enforces that constraint before calling
 * this. `discard` works for arbitrary mixed selections.
 */
export const bulkResolve = async (
  txIds: string[],
  resolution: ResolveAction,
): Promise<BulkResolveResult> => {
  const body: BulkResolveBody = { txIds, resolution }
  const response = await api.post<BulkResolveResult>(NEEDS_ATTENTION_ROUTES.BULK_RESOLVE, body)

  if (!response.success || !response.data) {
    throwBackendError(response, 'NEEDS_ATTENTION_BULK_RESOLVE_FAILED')
  }

  return response.data
}

/**
 * Reprocess unknown-operation staging rows against the CURRENT template rules.
 *
 * Rows that now classify are promoted to the ledger and leave the tray; the
 * rest remain `unknown_operation`. When `stagingRowIds` is omitted the backend
 * reprocesses ALL this-tenant unknown rows.
 *
 * Non-idempotent (writes ledger rows) — callers must NOT retry on failure.
 */
export const reprocessUnknownRows = async (
  request: ReprocessUnknownRowsRequest = {},
): Promise<ReprocessUnknownRowsResponse> => {
  const response = await api.post<ReprocessUnknownRowsResponse>(
    NEEDS_ATTENTION_ROUTES.REPROCESS_UNKNOWN,
    request,
  )

  if (!response.success || !response.data) {
    throwBackendError(response, 'NEEDS_ATTENTION_REPROCESS_FAILED')
  }

  return response.data
}
